import { Box, Button, Center, Input, Text } from "@chakra-ui/react";
import { ReactElement, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { SubmitHandler, useForm } from "react-hook-form";
import { voislogApi } from "../utils/apiClients/voislogApi";
import { ApiError } from "../utils/apiClients/ApiError";
import type { components } from "../types/voislog_api";
import { useAtom } from "jotai";
import { loginImageInfoAtom } from "../store";
import { useToastMessage } from "../hooks/useToastMessage";
import { BasicPageLayout } from "../components/templates/BasicPageLayout";
import { BannerImage } from "../components/atoms/BannerImage";
import { useModifiedTranslation } from "../hooks/useModifiedTranslation";

type PasswordResetFormData = components["schemas"]["PasswordResetRequest"];

type RequestFormProps = {
  onCompleted: () => void;
};

function RequestForm({
  onCompleted,
}: Readonly<RequestFormProps>): ReactElement {
  const t = useModifiedTranslation();
  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<PasswordResetFormData>();
  const { i18n } = useTranslation();
  const toastMessage = useToastMessage();

  const port = window.location.port ? ":" + window.location.port : "";
  const resetUrl =
    window.location.protocol +
    "//" +
    window.location.hostname +
    port +
    "/" +
    i18n.resolvedLanguage +
    "/password-reset/{verification_id}";

  const onSubmit: SubmitHandler<PasswordResetFormData> = useCallback(
    async (data) => {
      try {
        const { error, response } = await voislogApi.POST(
          "/v2/core/auth/password-reset/request",
          {
            // NOTE: app_url は非公開パラメータのため型キャストで渡す
            body: {
              email: data.email,
              language: data.language,
              app_url: resetUrl,
            },
          } as {
            body: components["schemas"]["PasswordResetRequest"] & {
              app_url: string;
            };
          },
        );
        if (error) throw new ApiError(response, "post", error);
        onCompleted();
      } catch (e) {
        console.error("Login Error!", e);
        toastMessage(e);
      }
    },
    [onCompleted, toastMessage, resetUrl],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <input
        hidden
        {...register("language", {
          value: i18n.resolvedLanguage as "ja" | "en",
        })}
      />
      <Text fontSize="sm">{t("PasswordReset.description.main")}</Text>
      <br />
      <Text fontSize="sm" whiteSpace="pre-wrap">
        {t("PasswordReset.description.attention")}
      </Text>
      <Center>
        <Input
          type="email"
          required
          data-testid="password-reset-email"
          {...register("email", { required: true })}
          placeholder={t("PasswordReset.emailPlaceholder")}
          my={20}
          rounded="full"
          width="80%"
          size="lg"
          bg="common.base"
        />
      </Center>
      <Text fontSize="sm">{t("PasswordReset.bottomAttention")}</Text>
      <Button
        variant="btn_primary"
        width="full"
        size="lg"
        my={10}
        type="submit"
        data-testid="password-reset-submit"
        isLoading={isSubmitting}
      >
        {t("PasswordReset.send")}
      </Button>
    </form>
  );
}

function Completed(): ReactElement {
  const t = useModifiedTranslation();
  return (
    <Box pt={10}>
      <Text align="center" whiteSpace="pre-wrap">
        {t("PasswordReset.request.completed")}
      </Text>
      <Center>
        <Button
          as={Link}
          to="../../login"
          variant="btn_primary"
          width="70%"
          mt={12}
        >
          {t("PasswordReset.login")}
        </Button>
      </Center>
    </Box>
  );
}

export function PasswordReset(): ReactElement {
  const t = useModifiedTranslation();
  const [isCompleted, setIsCompleted] = useState(false);

  // ログイン画面に表示する画像のURLをlocalStorageに保持管理用
  const [loginImageInfo] = useAtom(loginImageInfoAtom);

  return (
    <>
      {/* ログイン後にAPIで取得したアプリのロゴを表示 */}
      <BannerImage imageInfo={loginImageInfo} />
      <BasicPageLayout
        title={t("PasswordReset.title")}
        titleProps={{ fontSize: "2xl" }}
        showBackButton
      >
        {isCompleted ? (
          <Completed />
        ) : (
          <RequestForm onCompleted={() => setIsCompleted(true)} />
        )}
      </BasicPageLayout>
    </>
  );
}
